/**
 * Collects the Live API's audio output and wraps it in a WAV container.
 *
 * Live audio output is always raw, little-endian, 16-bit PCM at 24 kHz mono.
 * Because that format is fixed, the number of bytes received so far is an exact
 * position inside the generated track — which is what makes pose and caption
 * timing possible without any clock: a `toolCall` that arrives after N bytes
 * belongs at `N / (rate * bytesPerSample)` seconds. Wall-clock timestamps would
 * carry network jitter instead.
 */

export const OUTPUT_SAMPLE_RATE = 24_000;
export const BYTES_PER_SAMPLE = 2;
export const CHANNELS = 1;

const BYTES_PER_MS = (OUTPUT_SAMPLE_RATE * BYTES_PER_SAMPLE * CHANNELS) / 1000;

function decodeBase64({ base64 }: { base64: string }): Uint8Array {
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return bytes;
}

export class PcmAccumulator {
	/**
	 * Silence written before the narration. The model starts speaking on its
	 * first sample, so this is the only place a held opening frame can come
	 * from — and because it counts towards `elapsedMs`, every cue stamped
	 * afterwards already sits on the finished video's timeline.
	 */
	private readonly leadInBytes: number;
	private chunks: Uint8Array[] = [];
	private audioBytes = 0;

	constructor({ leadInMs = 0 }: { leadInMs?: number } = {}) {
		// Rounded to whole samples so the byte count stays an exact duration.
		this.leadInBytes =
			Math.round((leadInMs * OUTPUT_SAMPLE_RATE) / 1000) *
			BYTES_PER_SAMPLE *
			CHANNELS;
	}

	push({ base64 }: { base64: string }): void {
		const bytes = decodeBase64({ base64 });
		if (bytes.length === 0) return;
		this.chunks.push(bytes);
		this.audioBytes += bytes.length;
	}

	/**
	 * Throw away the narration received so far, keeping the lead-in.
	 *
	 * Used when the server cancels a generation: that audio is a discarded take
	 * and the model starts over, so keeping it would leave the narration in the
	 * file twice.
	 */
	reset(): void {
		this.chunks = [];
		this.audioBytes = 0;
	}

	/**
	 * Drop narration positioned before `atMs`, keeping the lead-in, and return
	 * how much was actually removed.
	 *
	 * The cut is made at an exact byte offset rather than a chunk boundary, so
	 * the returned figure is precise and callers can shift their own timestamps
	 * by it.
	 */
	dropAudioBefore({ atMs }: { atMs: number }): number {
		const wanted = Math.max(0, atMs - this.leadInBytes / BYTES_PER_MS);
		const cutBytes = Math.min(
			this.audioBytes,
			Math.round((wanted * OUTPUT_SAMPLE_RATE) / 1000) *
				BYTES_PER_SAMPLE *
				CHANNELS,
		);
		if (cutBytes <= 0) return 0;

		let remaining = cutBytes;
		const kept: Uint8Array[] = [];
		for (const chunk of this.chunks) {
			if (remaining >= chunk.length) {
				remaining -= chunk.length;
				continue;
			}
			kept.push(remaining > 0 ? chunk.subarray(remaining) : chunk);
			remaining = 0;
		}

		this.chunks = kept;
		this.audioBytes -= cutBytes;
		return cutBytes / BYTES_PER_MS;
	}

	get byteLength(): number {
		return this.leadInBytes + this.audioBytes;
	}

	/**
	 * Milliseconds of narration received, ignoring the lead-in — silence is not
	 * evidence that the model said anything.
	 */
	get audioMs(): number {
		return this.audioBytes / BYTES_PER_MS;
	}

	/** Playback position of everything received so far, in milliseconds. */
	get elapsedMs(): number {
		return this.byteLength / BYTES_PER_MS;
	}

	get durationSeconds(): number {
		return this.elapsedMs / 1000;
	}

	get isEmpty(): boolean {
		return this.audioBytes === 0;
	}

	toWavBlob(): Blob {
		const pcm = new ArrayBuffer(this.byteLength);
		const view = new Uint8Array(pcm);
		// The lead-in needs no writing: a fresh ArrayBuffer is already zeroed,
		// and zeroed 16-bit PCM is silence.
		let offset = this.leadInBytes;
		for (const chunk of this.chunks) {
			view.set(chunk, offset);
			offset += chunk.length;
		}
		return buildWav({ pcm });
	}
}

function buildWav({ pcm }: { pcm: ArrayBuffer }): Blob {
	const header = new ArrayBuffer(44);
	const view = new DataView(header);
	const byteRate = OUTPUT_SAMPLE_RATE * CHANNELS * BYTES_PER_SAMPLE;

	const writeAscii = ({ at, text }: { at: number; text: string }) => {
		for (let i = 0; i < text.length; i++) {
			view.setUint8(at + i, text.charCodeAt(i));
		}
	};

	writeAscii({ at: 0, text: "RIFF" });
	view.setUint32(4, 36 + pcm.byteLength, true);
	writeAscii({ at: 8, text: "WAVE" });
	writeAscii({ at: 12, text: "fmt " });
	view.setUint32(16, 16, true); // PCM chunk size
	view.setUint16(20, 1, true); // format: PCM
	view.setUint16(22, CHANNELS, true);
	view.setUint32(24, OUTPUT_SAMPLE_RATE, true);
	view.setUint32(28, byteRate, true);
	view.setUint16(32, CHANNELS * BYTES_PER_SAMPLE, true); // block align
	view.setUint16(34, BYTES_PER_SAMPLE * 8, true); // bits per sample
	writeAscii({ at: 36, text: "data" });
	view.setUint32(40, pcm.byteLength, true);

	return new Blob([header, pcm], { type: "audio/wav" });
}
